import { Link } from '@tanstack/react-router'
import { secondaryLinkClasses } from '@workspace/ui/components/dsm/secondary-button'
import { StatusRow } from '@workspace/ui/components/status-row'
import { cn } from '@workspace/ui/lib/utils'
import { Clock, Play, Send, Users } from 'lucide-react'

/**
 * The running campaign, in numbers.
 *
 * QUEST_LIVE is the state a founder sees most often and for longest, and it was
 * the one card with no visual at all — the page said "you are reaching
 * investors now" and then showed nothing to back it up. These three figures are
 * the ones the founder actually wants: is it connected, how far through the
 * queue is it, and how much is left.
 */
export function LiveVisual({
  sentTotal,
  queuedCount,
  senderConnected,
  pausedTrackLabel,
  warmIntroAvailable = 0,
  warmIntroEnabled = false,
  investorEnabled = true,
  matchCount = 0,
  startupId,
}: {
  sentTotal: number
  queuedCount: number
  senderConnected: boolean
  /** Set when one channel is off while the other still sends. */
  pausedTrackLabel?: string
  /** Founders on the warm-intro board the founder has not decided on yet. */
  warmIntroAvailable?: number
  /** Matched investors — the pool behind the investor campaign. */
  matchCount?: number
  /** The two channel switches. Whichever is off is the one being offered. */
  warmIntroEnabled?: boolean
  investorEnabled?: boolean
  startupId?: string
}) {
  const total = sentTotal + queuedCount

  // The card offers whichever channel is not running — that is the whole point
  // of it, and offering warm intros to a founder who is already running warm
  // intros (and not investors) described the wrong half of their campaign.
  // Both off is QUEST_PAUSED, not this state, so at most one offer applies.
  // Both on means the offer has been taken and the card goes away.
  const offer = !investorEnabled
    ? ({
        pool: matchCount,
        tab: 'investors',
        icon: <Send className="size-4" />,
        title: 'Your investor campaign is ready',
        detail: `${matchCount} investors match your thesis and are waiting to be asked. Each one gets a drafted intro you approve first.`,
      } as const)
    : !warmIntroEnabled
      ? ({
          pool: warmIntroAvailable,
          tab: 'portfolio',
          icon: <Users className="size-4" />,
          title: 'Your warm intro campaign is ready',
          detail: `${warmIntroAvailable} founders who raised from your matched investors are waiting to be asked. Approving them puts both campaigns to work.`,
        } as const)
      : null
  const showOffer = offer !== null && offer.pool > 0 && startupId

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-border/40 bg-card/40 px-5 py-4">
        <StatusRow
          tone={senderConnected ? 'live' : 'paused'}
          label={senderConnected ? 'Sending from your LinkedIn' : 'Sender not connected'}
          detail={senderConnected ? 'healthy · re-checks daily' : undefined}
          className="mb-3"
        />

        {/* One line, the way the founder would say it: how far through, and how
            much is waiting. Three stat tiles gave equal weight to a number
            ("contacts in this campaign") nobody was asking for. */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[13px] text-muted-foreground">
          <span>
            <b className="font-semibold text-foreground">{sentTotal}</b> sent of{' '}
            <b className="font-semibold text-foreground">{total}</b>
          </span>
          <span>
            <b className="font-semibold text-foreground">{queuedCount}</b> approved and queued
          </span>
        </div>

        {/* Only before the first send, which is exactly when a still counter
            looks broken. */}
        {senderConnected && sentTotal === 0 && (
          <p className="mt-3 flex items-start gap-2 text-[12.5px] text-muted-foreground leading-relaxed">
            <Clock className="mt-0.5 size-3.5 shrink-0" />
            Just connected? Warm-up can take up to 48 hours before your first connection requests go
            out, so the counter may hold still at first.
          </p>
        )}

        {pausedTrackLabel && (
          <p className="mt-3 text-[12.5px] text-muted-foreground">
            Your {pausedTrackLabel} campaign is off. The other one is still sending.
          </p>
        )}
      </div>

      {showOffer && (
        <Link
          to="/app/startup/$id/outreach"
          params={{ id: startupId }}
          search={{ tab: offer.tab } as never}
          className="flex items-start gap-3 rounded-xl border border-border/40 bg-card/40 px-5 py-4 transition-colors hover:bg-card/60"
        >
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
            {offer.icon}
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[14px] font-semibold text-foreground">{offer.title}</div>
            <p className="mt-0.5 text-[12.5px] text-muted-foreground leading-relaxed">
              {offer.detail}
            </p>
          </div>
          {/* The whole card is the link, so this is a span wearing the button's
              classes rather than a nested <button>. Same green as Resume on the
              outreach page: both turn a campaign on. */}
          <span
            className={cn(
              secondaryLinkClasses({ tone: 'success', size: 'sm' }),
              'shrink-0 self-center'
            )}
          >
            <Play className="size-3.5" />
            Resume
          </span>
        </Link>
      )}
    </div>
  )
}
